import React, { Fragment, useState, useEffect } from 'react'
import {
  Box,
  Button,
  Grid,
  IconButton,
  LinearProgress,
  List,
  Tooltip,
  Typography,
  Link,
  ListItemText,
  CircularProgress,
  ListItem,
  ListItemAvatar,
  Avatar,
  Divider
} from '@mui/material'
import { uploadOrderFile, downloadTemplate } from '../config/api'
import { Warehouse } from '../../../interfaces'
import { SelectWarehouse } from '../../../components/Warehouse'
import HelpIcon from '@mui/icons-material/Help'
import Info from '@mui/icons-material/Info'
import { FolderOpen as FolderIcon, Upload as UploadIcon } from '@mui/icons-material'
import { templateWorkOrderMassiveProcess } from '../../../const/templatesFileName'

interface OrderFileUploadProps {
  warehouses: Warehouse[]
  onSuccess: (message: string) => void
  onError: (message: string) => void
}

export const OrderFileUpload: React.FC<OrderFileUploadProps> = ({
  warehouses,
  onSuccess,
  onError
}) => {
  const maxFileSize = 2097152 // 2 MB

  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null)
  const [currentFile, setCurrentFile] = useState<File | null>(null)
  const [progress, setProgress] = useState<number>(0)
  const [disabledLink, setDisabledLink] = useState<boolean>(false)
  const [selectedWarehouse, setSelectedWarehouse] = useState<number>(0)

  useEffect(() => {
    if (warehouses?.length === 1) {
      setSelectedWarehouse(warehouses[0].id)
    }
  }, [warehouses])

  const handleSelectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFiles(event.target.files)
    setProgress(0)
  }

  const sizeLabel = (size: number): string => {
    if (size / 1024 / 1024 > 1) return (size / 1024 / 1024).toFixed(2) + ' MB'
    if (size / 1024 > 1) return (size / 1024).toFixed(0) + ' KB'
    return size.toFixed(2) + ' Bytes'
  }

  const validateFile = (files: FileList | null): string => {
    if (!files || files.length === 0) {
      return 'Ningún archivo seleccionado'
    }

    const selectedFile = files[0]

    if (selectedFile.size > maxFileSize) {
      const maxLabel = sizeLabel(maxFileSize)
      return `El archivo seleccionado supera el tamaño máximo: ${maxLabel}`
    }

    if (selectedFile.name.toLowerCase().indexOf('.xls') < 0) {
      return 'El archivo seleccionado no es de tipo Excel (.xls)'
    }

    return ''
  }

  const handleUpload = async () => {
    const validateError = validateFile(selectedFiles)
    if (validateError !== '') {
      setCurrentFile(null)
      onError(validateError)
      return
    }

    if (!selectedFiles) return

    const selectedFile = selectedFiles[0]
    setProgress(0)
    setCurrentFile(selectedFile)
    setIsUploading(true)

    try {
      const response = await uploadOrderFile(selectedFile, selectedWarehouse)

      setIsUploading(false)

      if (response.data.success) {
        onSuccess('El archivo ha sido cargado para su procesamiento')
      } else {
        onError(
          `Error en carga de archivo: ${
            response.data.message || 'falla en el servicio.'
          }`
        )
      }

      setCurrentFile(null)
      setSelectedFiles(null)
      setProgress(0)
    } catch (error) {
      setIsUploading(false)
      setProgress(0)
      onError(
        `Error en carga de archivo: ${
          (error as any)?.data?.message || 'falla en el servicio.'
        }`
      )
      setCurrentFile(null)
      setSelectedFiles(null)
      setProgress(0)
    }
  }

  const handleDownloadTemplate = async () => {
    setDisabledLink(true)

    try {
      const response = await downloadTemplate(templateWorkOrderMassiveProcess)

      if (response.status === 200 && response.data !== null) {
        const raw = window.atob(response.data.file)
        const uInt8Array = new Uint8Array(raw.length)
        for (let i = 0; i < raw.length; ++i) {
          uInt8Array[i] = raw.charCodeAt(i)
        }
        const link = document.createElement('a')
        const blob = new Blob([uInt8Array], {
          type: response.data.mimeType
        })
        link.style.display = 'none'
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', response.data.name)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (error) {
      console.error('Error descargando plantilla:', error)
      onError('Error al descargar la plantilla')
    } finally {
      setDisabledLink(false)
    }
  }

  return (
    <Fragment>

        <Typography variant="h4" component="h4" sx={{ textAlign: 'center' }}>
          Cargar filtro externo de pedidos
        </Typography>

        <Grid item xs={12} style={{ padding: '0px' }}>
          <List style={{ width: '100%' }}>
            <ListItem alignItems="flex-start">
              <ListItemAvatar>
                <Avatar>
                  <Info />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="h6" component="h6" gutterBottom>
                    El archivo Excel debe contener una columna con el ID de
                    pedido que permite crear ordenes de trabajo.
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography component="ol" style={{ listStyle: 'square' }}>
                      <Typography
                        variant="subtitle1"
                        component="li"
                        gutterBottom
                      >
                        Puede descargar una <b>plantilla</b> desde el link que
                        se encuentra disponible a continuación.
                        <br />
                        {disabledLink ? (
                          <Fragment>
                            <Link
                              style={{
                                cursor: 'pointer',
                                pointerEvents: 'none'
                              }}
                              onClick={handleDownloadTemplate}
                            >
                              Descargando Plantilla...
                            </Link>{' '}
                            <CircularProgress size="0.9rem" />
                          </Fragment>
                        ) : (
                          <Link
                            style={{ cursor: 'pointer' }}
                            onClick={handleDownloadTemplate}
                          >
                            Descargar Plantilla
                          </Link>
                        )}
                      </Typography>
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          </List>
        </Grid>

      <Grid
        item
        xs={12}
        justifyContent="center"
        alignItems="center"
        alignContent="center"
        style={{ textAlign: 'center' }}
      >
        <SelectWarehouse
          warehouses={warehouses}
          onChange={setSelectedWarehouse}
          selectedWarehouse={selectedWarehouse}
        />
      </Grid>
      <Divider sx={{ my: 2 }} />

      <Box className="mb25" display="flex" alignItems="center">
        <Grid
          container
          spacing={4}
          direction="row"
          alignItems="center"
          justifyContent="center"
          style={{ justifyContent: 'center' }}
        >
          <Grid item xs={3} style={{ textAlign: 'right' }}>
            <label htmlFor="btn-upload">
              <input
                id="btn-upload"
                name="btn-upload"
                style={{ display: 'none' }}
                type="file"
                onChange={handleSelectFile}
                accept=".xls,.xlsx"
              />
              <Tooltip
                title="Seleccionar el archivo Excel a cargar"
                placement="top-end"
              >
                <Button
                  className="btn-choose"
                  variant="outlined"
                  component="span"
                  size="small"
                  startIcon={<FolderIcon />}
                  sx={{ borderRadius: '10px', textTransform: 'none' }}
                >
                  Seleccionar Archivo
                </Button>
              </Tooltip>
            </label>
          </Grid>

          <Grid item xs={5}>
            <Typography
              variant="caption"
              sx={{ flex: 1, color: 'text.secondary' }}
              noWrap
            >
              {selectedFiles && selectedFiles.length > 0
                ? selectedFiles[0].name
                : 'Ningún archivo seleccionado'}
            </Typography>
          </Grid>

          <Grid item xs={1}>
            <Tooltip
              title="Enviar el archivo seleccionado al servidor para su procesamiento"
              placement="top-end"
            >
              <Button
                className="btn-upload"
                color="primary"
                variant="contained"
                component="span"
                size="small"
                startIcon={<UploadIcon />}
                sx={{ borderRadius: '10px', textTransform: 'none' }}
                disabled={
                  selectedWarehouse === 0 || !selectedFiles || isUploading
                }
                onClick={handleUpload}
              >
                Cargar
              </Button>
            </Tooltip>
          </Grid>

          <Grid
            item
            xs={1}
            alignContent="center"
            style={{ textIndent: '15px' }}
          >
            <Tooltip
              title="Oprima el botón SELECCIONAR para abrir el archivo a cargar y luego el botón CARGAR para enviar el archivo al servidor para su procesamiento"
              placement="top-end"
            >
              <IconButton>
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      <div
        style={{
          margin: '10px',
          visibility: currentFile ? 'visible' : 'hidden'
        }}
      >
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 15,
            borderRadius: '5px',
            backgroundColor: '#EEEEEE',
            '& .MuiLinearProgress-bar': {
              borderRadius: '5px',
              backgroundColor: '#1a90ff'
            }
          }}
        />

        <Box minWidth={35}>
          <Typography
            variant="body1"
            color="textSecondary"
            style={{ textAlign: 'end' }}
          >{`${progress}%`}</Typography>
        </Box>
      </div>
    </Fragment>
  )
}
